import React, { useMemo } from 'react';

import Box from '@amzn/meridian/box';
import { type BoxProps } from '@amzn/meridian/box/box';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Grid from 'src/components/shared/grid/grid';
import { MeasureUnitAbbreviations } from 'src/services/tasks/constants';
import { getWeightRestrictionTaskLabel, type WeightRestrictionTask } from 'src/utils/restrictionUtils';

// **** Constants **** //

const LABEL_TEST_ID = 'new-jmr__weight-restriction__child-weight-range-hours-selector-label';

// **** Types **** //

interface ChildSelectorsProps extends BoxProps {
  childSelection: WeightRestrictionTask[];
  onUpdateChildSelection: (childIndex: number, value: number) => void;
  shiftLength: number;
  isDisabled: boolean;
  /** @testOnly */
  index: number;
}

interface ChildSelectorProps {
  selectedValue: number;
  onSelectValue: (value: number) => void;
  shiftLength: number;
  errorMessage?: string;
  isDisabled: boolean;
  /** @testOnly */
  parentIndex: number;
  index?: number;
}

interface DropdownOption {
  label: string;
  value: number;
}

// **** Functions **** //

/**
 * Get the label
 */
const getChildSelectorOption = (index: number, shiftLength: number) => {
  const { Hours } = MeasureUnitAbbreviations;
  if (index === shiftLength - 1) {
    return { label: `${shiftLength} ${Hours} (Full Shift)`, value: shiftLength };
  } else {
    return { label: `${index + 1} ${Hours}`, value: index + 1 };
  }
};

// **** Components **** //

/**
 * Default component
 */
const ChildSelectors = ({
  childSelection,
  onUpdateChildSelection,
  shiftLength,
  isDisabled,
  index,
  ...boxProps
}: ChildSelectorsProps) => {
  return (
    <Box {...boxProps}>
      <Grid columns={2}>
        {childSelection.map((child, i) =>
          child.showSelectorEle ? (
            <Box key={i}>
              <Text data-cy={LABEL_TEST_ID} data-testid={LABEL_TEST_ID}>
                {getWeightRestrictionTaskLabel(child)}
              </Text>
              <ChildSelector
                selectedValue={Number(child.restrictedValue)}
                onSelectValue={(value) => onUpdateChildSelection(i, value)}
                shiftLength={shiftLength}
                isDisabled={isDisabled}
                errorMessage={child.error}
                parentIndex={index}
                index={i}
              />
            </Box>
          ) : null,
        )}
      </Grid>
    </Box>
  );
};

/**
 * Select hours value available based on the shift length.
 */
const ChildSelector = ({
  selectedValue,
  onSelectValue,
  shiftLength,
  isDisabled,
  errorMessage,
  parentIndex,
  index,
}: ChildSelectorProps) => {
  // This component does not make sense outside of the context of MeasureUnitValues.Hours
  // since we are quantizing all available selection values to be one hour increments
  // which may not be applicable to other measureUnit types. So "Hours" in hardcoded.
  const options: DropdownOption[] = useMemo(
    () => Array.from({ length: shiftLength }, (_, i) => getChildSelectorOption(i, shiftLength)),
    [shiftLength],
  );

  return (
    <Select
      data-cy={`new-jmr__restrictions__hours-selector__dropdown__${parentIndex}__${index}`}
      placeholder="Select hours"
      value={selectedValue}
      onChange={(v) => v !== selectedValue && onSelectValue(v)}
      errorMessage={errorMessage}
      error={!!errorMessage}
      disabled={isDisabled}
    >
      {options.map((o) => (
        <SelectOption
          key={o.value}
          label={o.label}
          value={o.value}
          data-cy="new-jmr__hours-selector__dropdown-item"
          data-testid="new-jmr__hours-selector__dropdown-item"
        />
      ))}
    </Select>
  );
};

// **** Export **** //

export default ChildSelectors;
